// src/misfortune.ts
// Misfortune – daily wheel-of-fortune game frontend entry point

interface Puzzle {
  type: string;
  answer: string;
}

interface DailyResult {
  date: string;
  puzzleType: string;
  points: number;
}

interface GuessEntry {
  letter: string;
  hit: boolean;
}

interface RevealAnimation {
  letter?: string;
  index?: number;
}

interface WheelSlice {
  label: string;
  weight: number;
  value?: number;
  isDoink?: boolean;
}

interface GameState {
  date: string;
  points: number;
  guessedLetters: string[];
  guessHistory: GuessEntry[];
  mustGuessAfterSpin: boolean;
  canBuyVowels: boolean;
  pendingSpinValue: number | null;
  revealedIndices: number[];
  hintUses: number;
  solved: boolean;
}

interface MisfortuneStore {
  gameState: GameState;
  dailies: DailyResult[];
  highScore: number;
}

const STORAGE_KEY = "misfortuneDailyStateV1";

const FALLBACK_PUZZLES: Puzzle[] = [
  { type: "Song Name", answer: "Take On Me" },
  { type: "Phrase", answer: "Break A Leg" },
  { type: "Saying", answer: "Bite The Bullet" },
  { type: "Quote", answer: "May The Force Be With You" },
  { type: "Before and After", answer: "Night Owl City" },
  { type: "Movie Title", answer: "Back To The Future" },
  { type: "Food", answer: "Peanut Butter" },
  { type: "Place", answer: "Golden Gate Bridge" },
  { type: "Thing", answer: "Pocket Watch" },
  { type: "Book Title", answer: "The Great Gatsby" },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const VOWELS = new Set(["A", "E", "I", "O", "U"]);

const MIN_STANDARD_WHEEL_VALUE = 100;
const MAX_STANDARD_WHEEL_VALUE = 1500;
const STANDARD_WHEEL_VALUE_STEP = 100;
const WRONG_SOLVE_PENALTY_RATIO = 0.5;
const VOWEL_COST = 250;
const HINT_COST_STEP = 500;

const STANDARD_WHEEL_SLICES: WheelSlice[] = [
  50,
  75,
  ...Array.from(
    {
      length:
        (MAX_STANDARD_WHEEL_VALUE - MIN_STANDARD_WHEEL_VALUE) /
          STANDARD_WHEEL_VALUE_STEP +
        1,
    },
    (_, index) => MIN_STANDARD_WHEEL_VALUE + index * STANDARD_WHEEL_VALUE_STEP
  ),
].map((value) => ({
  label: `${value}`,
  value,
  weight: 1,
}));

const FULL_SIZE_WHEEL_SLICES: WheelSlice[] = STANDARD_WHEEL_SLICES.concat([
  { label: "DOINK", weight: 1, isDoink: true },
  { label: "DOINK", weight: 1, isDoink: true },
  { label: "DOINK", weight: 1, isDoink: true },
]);

const NARROW_WHEEL_SLICE_CLUSTER: WheelSlice[] = [
  { label: "⅓ DOINK", weight: 1 / 3, isDoink: true },
  { label: "2500", value: 2500, weight: 1 / 3 },
  { label: "⅓ DOINK", weight: 1 / 3, isDoink: true },
];

const WHEEL_COLORS = [
  "#e74c3c",
  "#e67e22",
  "#f1c40f",
  "#27ae60",
  "#16a085",
  "#3498db",
  "#9b59b6",
  "#ff5c8a",
  "#4cc9f0",
  "#90be6d",
  "#f8961e",
  "#577590",
];

// Narrow slices are weighted below 1 (the ⅓-sized doink/2500 wedges).
// They need slightly farther label placement and smaller text to avoid clipping.
const NARROW_SLICE_LABEL_RADIUS_RATIO = 0.86;
const STANDARD_SLICE_LABEL_RADIUS_RATIO = 0.72;
const MIN_NARROW_SLICE_FONT_SIZE = 7;
const MIN_STANDARD_SLICE_FONT_SIZE = 9;
const MAX_WHEEL_LABEL_FONT_SIZE = 15;
const WHEEL_LABEL_FONT_SIZE_DIVISOR = 2.1;

// ───────────────── Helpers ─────────────────

function getLocalDateKey(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function hashDate(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function chooseDailyPuzzle(puzzleList: Puzzle[], date: string): Puzzle {
  const idx = hashDate(date) % puzzleList.length;
  return puzzleList[idx];
}

function normalizeLetter(input: string): string | null {
  const trimmed = input.trim().toUpperCase();
  return /^[A-Z]$/.test(trimmed) ? trimmed : null;
}

function normalizeSolveGuess(input: string): string {
  return input.toUpperCase().replace(/[^A-Z]/g, "");
}

function isVowel(letter: string): boolean {
  return VOWELS.has(letter);
}

function countHits(letter: string, answer: string): number {
  let count = 0;
  for (const char of answer.toUpperCase()) {
    if (char === letter) count += 1;
  }
  return count;
}

function shuffleArray<T>(items: T[]): T[] {
  const shuffled = items.slice();

  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

function createWheelSlices(): WheelSlice[] {
  const shuffled = shuffleArray(FULL_SIZE_WHEEL_SLICES);
  const insertAt = Math.floor(Math.random() * (shuffled.length + 1));
  return [
    ...shuffled.slice(0, insertAt),
    ...NARROW_WHEEL_SLICE_CLUSTER,
    ...shuffled.slice(insertAt),
  ];
}

function chooseWeightedSliceIndex(wheelSlices: WheelSlice[]): number {
  const totalWeight = wheelSlices.reduce((sum, slice) => sum + slice.weight, 0);
  let roll = Math.random() * totalWeight;

  for (let i = 0; i < wheelSlices.length; i++) {
    roll -= wheelSlices[i].weight;
    if (roll <= 0) return i;
  }

  return wheelSlices.length - 1;
}

function createWheelSegments(wheelSlices: WheelSlice[]) {
  const totalWeight = wheelSlices.reduce((sum, slice) => sum + slice.weight, 0);
  let startOffset = 0;

  return wheelSlices.map((slice) => {
    const angleSpan = (slice.weight / totalWeight) * 2 * Math.PI;
    const segment = {
      slice,
      startOffset,
      angleSpan,
      centerOffset: startOffset + angleSpan / 2,
    };
    startOffset += angleSpan;
    return segment;
  });
}

function isSolved(
  answer: string,
  guessedLetters: Set<string>,
  revealedIndices: Set<number> = new Set()
): boolean {
  const normalizedAnswer = answer.toUpperCase();

  for (let index = 0; index < normalizedAnswer.length; index++) {
    const char = normalizedAnswer[index];
    if (
      /[A-Z]/.test(char) &&
      !guessedLetters.has(char) &&
      !revealedIndices.has(index)
    ) {
      return false;
    }
  }
  return true;
}

function getHiddenLetterIndices(
  answer: string,
  guessedLetters: Set<string>,
  revealedIndices: Set<number>
): number[] {
  const normalizedAnswer = answer.toUpperCase();
  const hiddenIndices: number[] = [];

  for (let index = 0; index < normalizedAnswer.length; index++) {
    const char = normalizedAnswer[index];
    if (
      /[A-Z]/.test(char) &&
      !guessedLetters.has(char) &&
      !revealedIndices.has(index)
    ) {
      hiddenIndices.push(index);
    }
  }

  return hiddenIndices;
}

function getHintCost(hintUses: number): number {
  return (hintUses + 1) * HINT_COST_STEP;
}

// ───────────────── Storage ─────────────────

function createInitialState(date: string): GameState {
  return {
    date,
    points: 0,
    guessedLetters: [],
    guessHistory: [],
    mustGuessAfterSpin: false,
    canBuyVowels: false,
    pendingSpinValue: null,
    revealedIndices: [],
    hintUses: 0,
    solved: false,
  };
}

function createInitialStore(date: string): MisfortuneStore {
  return {
    gameState: createInitialState(date),
    dailies: [],
    highScore: 0,
  };
}

function loadStore(date: string): MisfortuneStore {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialStore(date);
    const parsed = JSON.parse(raw) as Partial<MisfortuneStore>;

    const dailies = Array.isArray(parsed.dailies)
      ? parsed.dailies.filter(
          (entry): entry is DailyResult =>
            typeof entry?.date === "string" &&
            typeof entry?.puzzleType === "string" &&
            typeof entry?.points === "number"
        )
      : [];

    const highScore =
      typeof parsed.highScore === "number" ? parsed.highScore : 0;

    const current = parsed.gameState;
    if (!current || current.date !== date) {
      return { gameState: createInitialState(date), dailies, highScore };
    }

    return {
      gameState: {
        date,
        points:
          typeof current.points === "number" ? Math.max(0, current.points) : 0,
        guessedLetters: Array.isArray(current.guessedLetters)
          ? current.guessedLetters.filter(
              (letter): letter is string => typeof letter === "string"
            )
          : [],
        guessHistory: Array.isArray(current.guessHistory)
          ? current.guessHistory.filter(
              (item): item is GuessEntry =>
                typeof item?.letter === "string" &&
                typeof item?.hit === "boolean"
            )
          : [],
        mustGuessAfterSpin: Boolean(current.mustGuessAfterSpin),
        canBuyVowels: Boolean(current.canBuyVowels),
        pendingSpinValue:
          typeof current.pendingSpinValue === "number"
            ? current.pendingSpinValue
            : null,
        revealedIndices: Array.isArray(current.revealedIndices)
          ? current.revealedIndices.filter(
              (value): value is number =>
                typeof value === "number" && Number.isInteger(value) && value >= 0
            )
          : [],
        hintUses:
          typeof current.hintUses === "number" && current.hintUses >= 0
            ? Math.floor(current.hintUses)
            : 0,
        solved: Boolean(current.solved),
      },
      dailies,
      highScore,
    };
  } catch {
    return createInitialStore(date);
  }
}

function saveStore(store: MisfortuneStore) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function recordDailyIfSolved(
  store: MisfortuneStore,
  puzzle: Puzzle,
  date: string
) {
  if (!store.gameState.solved) return;
  const existing = store.dailies.find((entry) => entry.date === date);
  if (existing) {
    existing.points = store.gameState.points;
    existing.puzzleType = puzzle.type;
  } else {
    store.dailies.push({
      date,
      puzzleType: puzzle.type,
      points: store.gameState.points,
    });
  }
  if (store.gameState.points > store.highScore) {
    store.highScore = store.gameState.points;
  }
}

// ───────────────── DOM setup ─────────────────

document.addEventListener("DOMContentLoaded", () => {
  const spinBtn = document.getElementById(
    "misfortuneSpinBtn"
  ) as HTMLButtonElement;
  const wheelResultEl = document.getElementById(
    "misfortuneWheelResult"
  ) as HTMLElement;
  const wheelCanvas = document.getElementById(
    "misfortuneWheelCanvas"
  ) as HTMLCanvasElement;
  const puzzleTypeEl = document.getElementById(
    "misfortunePuzzleType"
  ) as HTMLElement;
  const puzzleBoardEl = document.getElementById(
    "misfortunePuzzleBoard"
  ) as HTMLElement;
  const pointsEl = document.getElementById(
    "misfortunePoints"
  ) as HTMLElement;
  const statusEl = document.getElementById(
    "misfortuneStatus"
  ) as HTMLElement;
  const letterBoardEl = document.getElementById(
    "misfortuneLetterBoard"
  ) as HTMLElement;
  const solveToggleBtn = document.getElementById(
    "misfortuneSolveToggleBtn"
  ) as HTMLButtonElement;
  const solveForm = document.getElementById(
    "misfortuneSolveForm"
  ) as HTMLFormElement;
  const solveInput = document.getElementById(
    "misfortuneSolveInput"
  ) as HTMLInputElement;
  const solveSubmitBtn = document.getElementById(
    "misfortuneSolveSubmitBtn"
  ) as HTMLButtonElement;
  const solveCancelBtn = document.getElementById(
    "misfortuneSolveCancelBtn"
  ) as HTMLButtonElement;
  const dailiesEl = document.getElementById(
    "misfortuneDailies"
  ) as HTMLElement;
  const highScoreEl = document.getElementById(
    "misfortuneHighScore"
  ) as HTMLElement;
  const shareBtn = document.getElementById(
    "misfortuneShareBtn"
  ) as HTMLButtonElement;
  const shareRow = document.querySelector(
    ".misfortune-share-row"
  ) as HTMLElement;
  const randomPuzzleBtn = document.getElementById(
    "misfortuneRandomBtn"
  ) as HTMLButtonElement;
  const buyVowelSection = document.getElementById(
    "misfortuneBuyVowelSection"
  ) as HTMLElement;
  const vowelBtns = Array.from(
    document.querySelectorAll<HTMLButtonElement>(".misfortune-vowel-btn")
  );
  const hintBtn = document.getElementById(
    "misfortuneHintBtn"
  ) as HTMLButtonElement;

  const today = getLocalDateKey();
  let puzzles: Puzzle[] = FALLBACK_PUZZLES;
  let activePuzzle: Puzzle = chooseDailyPuzzle(FALLBACK_PUZZLES, today);
  let store: MisfortuneStore = loadStore(today);
  let isSolveFormOpen = false;

  // ───────── Random mode ─────────

  interface RandomState {
    puzzle: Puzzle;
    gameState: GameState;
  }

  let isRandomMode = false;
  let randomState: RandomState | null = null;

  function currentGameState(): GameState {
    return isRandomMode && randomState ? randomState.gameState : store.gameState;
  }

  function currentPuzzle(): Puzzle {
    return isRandomMode && randomState ? randomState.puzzle : activePuzzle;
  }

  function saveCurrentState() {
    if (!isRandomMode) saveStore(store);
  }

  // ───────── Canvas wheel ─────────

  let currentWheelAngle = 0; // updated to the target normalized angle [0, 2π) after each spin
  let isWheelSpinning = false;

  const WHEEL_SLICES = createWheelSlices();
  const WHEEL_SEGMENTS = createWheelSegments(WHEEL_SLICES);

  const TILE_REVEAL_DELAY_MS = 70;

  function normalizeAngle(angle: number): number {
    return ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  }

  function drawWheel(angle: number) {
    if (!wheelCanvas) return;
    const ctx = wheelCanvas.getContext("2d");
    if (!ctx) return;

    const w = wheelCanvas.width;
    const h = wheelCanvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const outerRadius = Math.min(cx, cy) - 4;
    const hubRadius = outerRadius * 0.1;

    ctx.clearRect(0, 0, w, h);

    // Outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, outerRadius + 3, 0, 2 * Math.PI);
    ctx.fillStyle = "#23253a";
    ctx.fill();

    // Slices
    WHEEL_SEGMENTS.forEach(({ slice, startOffset, angleSpan, centerOffset }, i) => {
      const startA = angle + startOffset - Math.PI / 2;
      const endA = startA + angleSpan;
      const midA = angle + centerOffset - Math.PI / 2;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, outerRadius, startA, endA);
      ctx.closePath();
      ctx.fillStyle = slice.isDoink
        ? i % 2 === 0
          ? "#b5173f"
          : "#8d1b3d"
        : WHEEL_COLORS[i % WHEEL_COLORS.length];
      ctx.fill();

      ctx.strokeStyle = "rgba(0,0,0,0.4)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Label
      ctx.save();
      const labelRadius =
        outerRadius *
        (slice.weight < 1
          ? NARROW_SLICE_LABEL_RADIUS_RATIO
          : STANDARD_SLICE_LABEL_RADIUS_RATIO);
      ctx.translate(
        cx + Math.cos(midA) * labelRadius,
        cy + Math.sin(midA) * labelRadius
      );
      ctx.rotate(midA + Math.PI / 2);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#fff";
      const fontSize = Math.max(
        slice.weight < 1
          ? MIN_NARROW_SLICE_FONT_SIZE
          : MIN_STANDARD_SLICE_FONT_SIZE,
        Math.min(
          MAX_WHEEL_LABEL_FONT_SIZE,
          Math.floor((outerRadius * angleSpan) / WHEEL_LABEL_FONT_SIZE_DIVISOR)
        )
      );
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillText(slice.isDoink ? "💥" : slice.label, 0, 0);
      ctx.restore();
    });

    // Center hub
    ctx.beginPath();
    ctx.arc(cx, cy, hubRadius, 0, 2 * Math.PI);
    ctx.fillStyle = "#0a0c14";
    ctx.fill();
    ctx.strokeStyle = "#5b60a0";
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  function animateWheelSpin(sliceIndex: number, onComplete: () => void) {
    const targetSegment = WHEEL_SEGMENTS[sliceIndex];
    const rawTarget = -targetSegment.centerOffset;
    const targetNorm = normalizeAngle(rawTarget);

    // Forward delta from current angle to target plus extra full rotations
    let delta =
      (targetNorm - normalizeAngle(currentWheelAngle) + 2 * Math.PI) %
      (2 * Math.PI);
    if (delta < 0.2) delta += 2 * Math.PI;

    const extraSpins = 4 + Math.floor(Math.random() * 3);
    const totalDelta = extraSpins * 2 * Math.PI + delta;

    const startAngle = currentWheelAngle;
    const duration = 3500;
    const startTime = performance.now();

    function easeOut(t: number): number {
      return 1 - Math.pow(1 - t, 4);
    }

    function frame(now: number) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const angle = startAngle + totalDelta * easeOut(t);
      drawWheel(angle);

      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        currentWheelAngle = targetNorm;
        drawWheel(currentWheelAngle);
        isWheelSpinning = false;
        onComplete();
      }
    }

    isWheelSpinning = true;
    requestAnimationFrame(frame);
  }

  // ───────── Board tiles ─────────

  function renderBoard(newlyRevealedLetter?: string) {
    const gs = currentGameState();
    const guessedSet = new Set(gs.guessedLetters);
    const revealedSet = new Set(gs.revealedIndices);
    const answer = currentPuzzle().answer.toUpperCase();
    const revealAll = gs.solved;

    puzzleBoardEl.innerHTML = "";

    const words = answer.split(" ");
    let revealDelay = 0;
    let charIndex = 0;

    words.forEach((word, wordIdx) => {
      if (wordIdx > 0) {
        charIndex++; // account for the space
        const spacer = document.createElement("span");
        spacer.className = "misfortune-word-spacer";
        puzzleBoardEl.appendChild(spacer);
      }

      const wordSpan = document.createElement("span");
      wordSpan.className = "misfortune-word";

      for (const char of word) {
        const tile = document.createElement("span");
        const currentIndex = charIndex;
        charIndex++;

        if (/[A-Z]/.test(char)) {
          const isLetterGuessed = revealAll || guessedSet.has(char);
          const isPositionRevealed = revealedSet.has(currentIndex);
          const isRevealed = isLetterGuessed || isPositionRevealed;
          tile.className = "misfortune-tile" + (isRevealed ? " revealed" : "");
          if (isPositionRevealed && !guessedSet.has(char) && !revealAll) {
            tile.classList.add("hint-revealed");
          }
          tile.textContent = isRevealed ? char : "";
          if (
            isRevealed &&
            newlyRevealedLetter &&
            char === newlyRevealedLetter &&
            guessedSet.has(char)
          ) {
            tile.classList.add("tile-reveal");
            tile.style.animationDelay = `${revealDelay * TILE_REVEAL_DELAY_MS}ms`;
            revealDelay++;
          }
        } else {
          tile.className = "misfortune-tile special";
          tile.textContent = char;
        }

        wordSpan.appendChild(tile);
      }

      puzzleBoardEl.appendChild(wordSpan);
    });
  }

  // ───────── Render ─────────

  function canSpinNow(): boolean {
    return !currentGameState().solved && !isWheelSpinning && !currentGameState().mustGuessAfterSpin;
  }

  function canGuessNow(): boolean {
    return !currentGameState().solved && !isWheelSpinning && currentGameState().mustGuessAfterSpin;
  }

  function toggleSolveForm(nextOpen: boolean) {
    const gs = currentGameState();
    isSolveFormOpen = nextOpen && !gs.solved;
    solveForm.hidden = !isSolveFormOpen;
    solveToggleBtn.disabled = gs.solved || isWheelSpinning;
    solveToggleBtn.textContent = isSolveFormOpen ? "✖ Close solve" : "🧩 Solve";

    if (!isSolveFormOpen) {
      solveInput.value = "";
      return;
    }

    requestAnimationFrame(() => {
      solveInput.focus();
      solveInput.select();
    });
  }

  function renderLetterBoard() {
    const guessedSet = new Set(currentGameState().guessedLetters);
    const canGuess = canGuessNow();

    if (letterBoardEl.childElementCount === 0) {
      for (const letter of LETTERS) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "misfortune-letter-btn";
        button.dataset.letter = letter;
        button.textContent = letter;
        letterBoardEl.appendChild(button);
      }
    }

    Array.from(letterBoardEl.children).forEach((child) => {
      const button = child as HTMLButtonElement;
      const letter = button.dataset.letter ?? "";
      const alreadyUsed = guessedSet.has(letter);

      button.disabled = alreadyUsed || !canGuess;
      button.classList.toggle("used", alreadyUsed);
      button.classList.toggle("ready", !alreadyUsed && canGuess);
    });
  }

  function render(newlyRevealedLetter?: string) {
    const gs = currentGameState();
    puzzleTypeEl.textContent = currentPuzzle().type;
    renderBoard(newlyRevealedLetter);
    renderLetterBoard();
    pointsEl.textContent = `${gs.points}`;
    highScoreEl.textContent = `${store.highScore}`;
    dailiesEl.textContent = `${store.dailies.length}`;

    const canSpin = canSpinNow();
    const canGuess = canGuessNow();
    const canSolve = !gs.solved && !isWheelSpinning;

    spinBtn.disabled = !canSpin;
    solveToggleBtn.disabled = !canSolve;
    solveInput.disabled = !canSolve;
    solveSubmitBtn.disabled = !canSolve;
    solveCancelBtn.disabled = !canSolve;

    if (!canSolve && isSolveFormOpen) {
      toggleSolveForm(false);
    }

    // Show share row only when current game is solved
    shareRow.hidden = !gs.solved;

    // Show random puzzle button when the current puzzle (daily or random) is solved
    randomPuzzleBtn.hidden = !gs.solved;
    if (gs.solved) {
      randomPuzzleBtn.textContent = isRandomMode ? "🎲 New Random" : "🎲 Random Puzzle";
    }

    // Buy vowel section
    const guessedSet = new Set(gs.guessedLetters);
    const revealedSet = new Set(gs.revealedIndices);
    const canBuyVowels = gs.canBuyVowels && !gs.solved;
    buyVowelSection.hidden = !canBuyVowels;
    if (canBuyVowels) {
      vowelBtns.forEach((btn) => {
        const v = btn.dataset.vowel ?? "";
        const alreadyUsed = guessedSet.has(v);
        btn.disabled = alreadyUsed || gs.points < VOWEL_COST;
        btn.classList.toggle("used", alreadyUsed);
      });
    }

    // Hint button
    const hiddenCount = getHiddenLetterIndices(
      currentPuzzle().answer,
      guessedSet,
      revealedSet
    ).length;
    const nextHintCost = getHintCost(gs.hintUses);
    hintBtn.disabled =
      gs.solved || gs.points < nextHintCost || hiddenCount === 0;
    hintBtn.textContent = `💡 Hint (${nextHintCost} pts)`;

    if (gs.solved) {
      statusEl.textContent = isRandomMode
        ? `Random puzzle done! Score: ${gs.points} pts. 🎲`
        : `Puzzle solved! Final score: ${gs.points} points. 🎉`;
    } else if (!canGuess && !isSolveFormOpen) {
      solveToggleBtn.textContent = "🧩 Solve";
    }
  }

  function setStatus(message: string) {
    statusEl.textContent = message;
  }

  // ───────── Spin ─────────

  function spinWheel() {
    if (!canSpinNow()) return;

    const resultIndex = chooseWeightedSliceIndex(WHEEL_SLICES);
    const result = WHEEL_SLICES[resultIndex];

    const gs = currentGameState();
    gs.canBuyVowels = false;

    spinBtn.disabled = true;
    toggleSolveForm(false);
    setStatus("Spinning…");

    animateWheelSpin(resultIndex, () => {
      const gs2 = currentGameState();
      if (result.isDoink) {
        gs2.points = 0;
        gs2.pendingSpinValue = null;
        gs2.mustGuessAfterSpin = false;
        wheelResultEl.textContent = `${result.label} 💥`;
        setStatus(`${result.label}! Your points were reset to 0. Spin again.`);
      } else {
        gs2.pendingSpinValue = result.value ?? null;
        gs2.mustGuessAfterSpin = true;
        wheelResultEl.textContent = result.label;
        setStatus(`You spun ${result.label}! Pick one letter.`);
      }

      saveCurrentState();
      render();
    });
  }

  // ───────── Guess ─────────

  function guessLetter(letter: string) {
    const gs = currentGameState();
    if (gs.solved) return;
    if (!canGuessNow()) {
      setStatus("Spin the wheel first before guessing.");
      return;
    }

    if (gs.guessedLetters.includes(letter)) {
      setStatus(`You already guessed ${letter}. Try a different letter.`);
      return;
    }

    const hits = countHits(letter, currentPuzzle().answer);
    const spinValue = gs.pendingSpinValue ?? 0;
    if (spinValue <= 0) {
      gs.mustGuessAfterSpin = false;
      gs.pendingSpinValue = null;
      setStatus("Spin the wheel to get a valid letter value before guessing.");
      saveCurrentState();
      render();
      return;
    }

    if (hits > 0) {
      gs.points += spinValue * hits;
    } else {
      gs.points = Math.max(0, gs.points - spinValue);
    }

    gs.mustGuessAfterSpin = false;
    gs.pendingSpinValue = null;
    gs.canBuyVowels = true;

    gs.guessedLetters.push(letter);
    gs.guessHistory.push({ letter, hit: hits > 0 });

    const guessedSet = new Set(gs.guessedLetters);
    gs.solved = isSolved(currentPuzzle().answer, guessedSet, new Set(gs.revealedIndices));

    if (gs.solved) {
      gs.mustGuessAfterSpin = false;
      gs.pendingSpinValue = null;
      gs.canBuyVowels = false;
      toggleSolveForm(false);
      if (!isRandomMode) {
        recordDailyIfSolved(store, activePuzzle, today);
      }
      setStatus(
        isRandomMode
          ? `Random puzzle done! You scored ${gs.points} pts. 🎲`
          : `Puzzle solved! You scored ${gs.points} points. 🎉`
      );
    } else if (hits > 0) {
      setStatus(
        `${letter} appears ${hits} time${hits === 1 ? "" : "s"}! Buy vowels or spin again.`
      );
    } else {
      setStatus(
        `${letter} is not in the puzzle. You lost ${spinValue} points. Buy vowels or spin again.`
      );
    }

    saveCurrentState();
    render(letter);
  }

  function submitSolve(event: SubmitEvent) {
    event.preventDefault();

    const gs = currentGameState();
    if (gs.solved || isWheelSpinning) return;

    const guess = normalizeSolveGuess(solveInput.value);
    if (!guess) {
      setStatus("Enter a solve guess before submitting.");
      return;
    }

    const answer = normalizeSolveGuess(currentPuzzle().answer);
    solveInput.value = "";

    if (guess === answer) {
      gs.solved = true;
      gs.mustGuessAfterSpin = false;
      gs.pendingSpinValue = null;
      toggleSolveForm(false);
      if (!isRandomMode) {
        recordDailyIfSolved(store, activePuzzle, today);
      }
      saveCurrentState();
      render();
      setStatus(
        isRandomMode
          ? `Random puzzle done! You scored ${gs.points} pts. 🎲`
          : `Puzzle solved! You scored ${gs.points} points. 🎉`
      );
      return;
    }

    gs.points = Math.floor(gs.points * WRONG_SOLVE_PENALTY_RATIO);
    toggleSolveForm(false);
    saveCurrentState();
    render();
    setStatus(
      `Wrong solve. Your score was cut to ${gs.points}. Keep playing.`
    );
  }

  // ───────── Buy Vowel ─────────

  function buyVowel(vowel: string) {
    const gs = currentGameState();
    if (gs.solved) return;
    if (!gs.canBuyVowels) {
      setStatus("Make a normal letter guess after spinning before buying vowels.");
      return;
    }
    if (gs.guessedLetters.includes(vowel)) {
      setStatus(`${vowel} is already on the board. Pick a different vowel.`);
      return;
    }
    if (gs.points < VOWEL_COST) {
      setStatus(`Not enough points. Vowels cost ${VOWEL_COST} pts.`);
      return;
    }

    const hits = countHits(vowel, currentPuzzle().answer);
    gs.points -= VOWEL_COST;
    gs.guessedLetters.push(vowel);
    gs.guessHistory.push({ letter: vowel, hit: hits > 0 });

    const guessedSet = new Set(gs.guessedLetters);
    gs.solved = isSolved(currentPuzzle().answer, guessedSet, new Set(gs.revealedIndices));

    if (gs.solved) {
      gs.mustGuessAfterSpin = false;
      gs.pendingSpinValue = null;
      gs.canBuyVowels = false;
      toggleSolveForm(false);
      if (!isRandomMode) recordDailyIfSolved(store, activePuzzle, today);
      setStatus(
        isRandomMode
          ? `Random puzzle done! You scored ${gs.points} pts. 🎲`
          : `Puzzle solved! You scored ${gs.points} points. 🎉`
      );
    } else if (hits > 0) {
      setStatus(
        `Bought ${vowel}! It appears ${hits} time${hits === 1 ? "" : "s"}. Cost: ${VOWEL_COST} pts.`
      );
    } else {
      setStatus(`${vowel} is not in the puzzle. Spent ${VOWEL_COST} pts.`);
    }

    saveCurrentState();
    render(vowel);
  }

  // ───────── Hint ─────────

  function useHint() {
    const gs = currentGameState();
    if (gs.solved) return;

    const guessedSet = new Set(gs.guessedLetters);
    const revealedSet = new Set(gs.revealedIndices);
    const hidden = getHiddenLetterIndices(currentPuzzle().answer, guessedSet, revealedSet);

    if (hidden.length === 0) {
      setStatus("Nothing left to hint — all letters are visible!");
      return;
    }

    const cost = getHintCost(gs.hintUses);
    if (gs.points < cost) {
      setStatus(`Not enough points. Next hint costs ${cost} pts.`);
      return;
    }

    const targetIndex = hidden[Math.floor(Math.random() * hidden.length)];
    gs.points -= cost;
    gs.revealedIndices.push(targetIndex);
    gs.hintUses++;

    gs.solved = isSolved(
      currentPuzzle().answer,
      guessedSet,
      new Set(gs.revealedIndices)
    );

    if (gs.solved) {
      gs.mustGuessAfterSpin = false;
      gs.pendingSpinValue = null;
      gs.canBuyVowels = false;
      toggleSolveForm(false);
      if (!isRandomMode) recordDailyIfSolved(store, activePuzzle, today);
      setStatus(
        isRandomMode
          ? `Random puzzle done! You scored ${gs.points} pts. 🎲`
          : `Puzzle solved! You scored ${gs.points} points. 🎉`
      );
    } else {
      const nextCost = getHintCost(gs.hintUses);
      setStatus(
        `Hint used! One spot revealed. Cost: ${cost} pts. Next hint: ${nextCost} pts.`
      );
    }

    saveCurrentState();
    render();
  }

  // ───────── Share ─────────

  async function shareResult() {
    const gs = currentGameState();
    if (!gs.solved) return;
    const squares = gs.guessHistory
      .map((entry) => (entry.hit ? "🟩" : "⬛"))
      .join("");
    const label = isRandomMode ? "Misfortune (Random)" : `Misfortune ${today}`;
    const lines = [
      label,
      `${currentPuzzle().type} • ${gs.points} pts`,
      `Guesses: ${gs.guessHistory.length}`,
      squares,
    ];
    const text = lines.join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setStatus("Copied! Share it with your friends. 📋");
    } catch {
      setStatus("Copy failed. Here's your result:\n" + text);
    }
  }

  // ───────── Load puzzles ─────────

  async function loadPuzzles(): Promise<Puzzle[]> {
    try {
      const response = await fetch("./assets/misfortune-puzzles.json");
      if (!response.ok) throw new Error("HTTP " + response.status);
      const data: unknown = await response.json();
      if (!Array.isArray(data)) throw new Error("not array");
      const valid = data.filter(
        (item): item is Puzzle =>
          typeof (item as Puzzle)?.type === "string" &&
          (item as Puzzle).type.trim().length > 0 &&
          typeof (item as Puzzle)?.answer === "string" &&
          (item as Puzzle).answer.trim().length > 0
      );
      return valid.length > 0 ? valid : FALLBACK_PUZZLES;
    } catch {
      return FALLBACK_PUZZLES;
    }
  }

  // ───────── Random mode ─────────

  function startRandomMode() {
    const dailyAnswer = activePuzzle.answer;
    const pool = puzzles.filter((p) => p.answer !== dailyAnswer);
    const candidates = pool.length > 0 ? pool : puzzles;
    const rndPuzzle = candidates[Math.floor(Math.random() * candidates.length)];

    randomState = {
      puzzle: rndPuzzle,
      gameState: createInitialState(today),
    };
    isRandomMode = true;

    // Reset wheel visual state
    currentWheelAngle = 0;
    drawWheel(currentWheelAngle);
    wheelResultEl.textContent = "—";
    toggleSolveForm(false);
    letterBoardEl.innerHTML = "";
    setStatus("🎲 Random puzzle! Spin to start. (Not ranked — just for fun)");
    render();
  }

  // ───────── Init ─────────

  async function init() {
    puzzles = await loadPuzzles();
    activePuzzle = chooseDailyPuzzle(puzzles, today);
    store = loadStore(today);

    const guessedSet = new Set(store.gameState.guessedLetters);
    if (!store.gameState.solved && isSolved(activePuzzle.answer, guessedSet, new Set(store.gameState.revealedIndices))) {
      store.gameState.solved = true;
      store.gameState.mustGuessAfterSpin = false;
      store.gameState.pendingSpinValue = null;
      recordDailyIfSolved(store, activePuzzle, today);
    }

    saveStore(store);

    // Draw the initial wheel state
    drawWheel(currentWheelAngle);
    toggleSolveForm(false);

    if (!store.gameState.solved) {
      setStatus("Spin the wheel to start your daily Misfortune round!");
    }
    render();
  }

  // ───────── Events ─────────

  spinBtn.addEventListener("click", spinWheel);
  letterBoardEl.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;

    const letter = normalizeLetter(target.dataset.letter ?? "");
    if (!letter) return;

    guessLetter(letter);
  });
  solveToggleBtn.addEventListener("click", () => {
    toggleSolveForm(!isSolveFormOpen);
  });
  solveForm.addEventListener("submit", submitSolve);
  solveCancelBtn.addEventListener("click", () => {
    toggleSolveForm(false);
  });
  shareBtn.addEventListener("click", () => {
    void shareResult();
  });
  randomPuzzleBtn.addEventListener("click", () => {
    startRandomMode();
  });
  vowelBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      const vowel = btn.dataset.vowel ?? "";
      if (vowel) buyVowel(vowel);
    });
  });
  hintBtn.addEventListener("click", () => {
    useHint();
  });

  void init();
});
